/**
 * Shared logic: restrict catalog dropdown options (RFI services, questionnaire templates)
 * to entries that appear in the user's actual list data (all pages).
 */

/**
 * @param {Array<{ capabilities?: Array<{ rfi_id?: string|number, rfi_name?: string, services?: string }> }>} items
 * @returns {{ idSet: Set<string>, nameSet: Set<string> }}
 */
export function collectCapabilityIdentifierSets(items) {
  const idSet = new Set();
  const nameSet = new Set();
  if (!Array.isArray(items)) return { idSet, nameSet };

  for (const row of items) {
    const caps = row?.capabilities;
    if (!Array.isArray(caps)) continue;
    for (const cap of caps) {
      if (cap?.rfi_id != null && cap.rfi_id !== "") {
        idSet.add(String(cap.rfi_id));
      }
      const label = cap?.rfi_name || cap?.services;
      if (label) nameSet.add(label);
    }
  }
  return { idSet, nameSet };
}

/**
 * @param {Array} catalogRfis - entries from RFI list-for-filters API
 * @param {Array} items - rows with optional `capabilities`
 */
export function filterRfiCatalogByCapabilityRows(catalogRfis, items) {
  const { idSet, nameSet } = collectCapabilityIdentifierSets(items);
  if (!catalogRfis?.length || (idSet.size === 0 && nameSet.size === 0)) {
    return [];
  }
  return catalogRfis.filter((r) => {
    if (r.rfi_id != null && idSet.has(String(r.rfi_id))) return true;
    const nm = r.services || r.rfi_name || r.name;
    return Boolean(nm && nameSet.has(nm));
  });
}

/**
 * While background "all pages" data is loading, show the full catalog; then narrow to usage.
 */
export function buildRfiServiceFilterOptions(catalogRfis, items, isAllDataFetched) {
  if (!isAllDataFetched) return catalogRfis ?? [];
  const matched = filterRfiCatalogByCapabilityRows(catalogRfis, items);
  return matched.length > 0 ? matched : [];
}

/** Collects template / questionnaire identifiers (RFI rows and catalog rows use different field names). */
function collectRfiTemplateKeys(row) {
  const keys = [];
  const push = (v) => {
    if (v != null && v !== "") keys.push(String(v));
  };
  push(row?.templateId);
  push(row?.TemplateId);
  push(row?.hash_id);
  push(row?.Hash_Id);
  push(row?.hashId);
  push(row?.HashId);
  push(row?.template_id);
  push(row?.Template_Id);
  push(row?.questionnaire_id);
  push(row?.Questionnaire_Id);
  push(row?.id);
  push(row?.Id);
  if (Array.isArray(row?.capabilities)) {
    for (const cap of row.capabilities) {
      push(cap?.rfi_id);
      push(cap?.rfi_template_id);
      push(cap?.Rfi_Id);
      push(cap?.template_id);
      push(cap?.Template_Id);
    }
  }
  return keys;
}

function rfiTemplateLabel(row) {
  return row?.services ?? row?.Services;
}

/** Primary template id on an RFI row for filtering (camelCase or PascalCase; may fall back to capability ids). */
function rfiTemplateFilterValue(row) {
  const direct =
    row?.templateId ??
    row?.TemplateId ??
    row?.hash_id ??
    row?.Hash_Id ??
    row?.hashId ??
    row?.HashId ??
    row?.template_id ??
    row?.Template_Id ??
    row?.questionnaire_id ??
    row?.Questionnaire_Id;
  if (direct != null && direct !== "") return direct;
  const caps = row?.capabilities;
  if (!Array.isArray(caps)) return undefined;
  for (const cap of caps) {
    const v =
      cap?.rfi_template_id ??
      cap?.template_id ??
      cap?.Template_Id ??
      cap?.rfi_id ??
      cap?.Rfi_Id;
    if (v != null && v !== "") return v;
  }
  return undefined;
}

/**
 * Uses each RFI row's badge label (`services`) for options that match that row's template ids
 * so the dropdown matches the table, not only long catalog questionnaire names.
 */
function applyRfiTemplateLabelsToOptions(options, rfiItems) {
  const idToLabel = new Map();
  for (const row of rfiItems ?? []) {
    const label = rfiTemplateLabel(row);
    if (!label) continue;
    const fv = rfiTemplateFilterValue(row);
    if (fv != null && fv !== "") {
      idToLabel.set(String(fv), label);
    }
    for (const k of collectRfiTemplateKeys(row)) {
      if (!idToLabel.has(k)) idToLabel.set(k, label);
    }
  }
  return (options ?? []).map((opt) => {
    const keys = collectRfiTemplateKeys(opt);
    let badge = null;
    const optFv =
      opt?.hash_id ??
      opt?.templateId ??
      opt?.TemplateId ??
      opt?.id ??
      opt?.questionnaire_id ??
      opt?.Questionnaire_Id;
    if (optFv != null && optFv !== "") {
      badge = idToLabel.get(String(optFv));
    }
    if (!badge) {
      for (const k of keys) {
        if (idToLabel.has(k)) {
          badge = idToLabel.get(k);
          break;
        }
      }
    }
    if (badge) {
      return { ...opt, label: badge, name: badge };
    }
    return opt;
  });
}

/**
 * @param {Array} questionnaires - full template list
 * @param {Array} rfiItems - user RFI rows (e.g. from getMyRfis all pages)
 * @param {(q: object) => object} mapToOption
 */
export function filterQuestionnairesByRfiRows(questionnaires, rfiItems, mapToOption) {
  const items = rfiItems ?? [];
  const templateNames = new Set(items.map((i) => rfiTemplateLabel(i)).filter(Boolean));
  const templateHashIds = new Set();
  const questionnaireIds = new Set();
  items.forEach((i) => {
    collectRfiTemplateKeys(i).forEach((k) => templateHashIds.add(k));
    if (i.questionnaire_id != null && i.questionnaire_id !== "") {
      questionnaireIds.add(String(i.questionnaire_id));
    }
    if (i.Questionnaire_Id != null && i.Questionnaire_Id !== "") {
      questionnaireIds.add(String(i.Questionnaire_Id));
    }
  });
  if (!questionnaires?.length) return [];

  const matchesUserRfis = (q) => {
    const catalogQId =
      q.id != null
        ? String(q.id)
        : q.questionnaire_id != null
          ? String(q.questionnaire_id)
          : null;
    if (catalogQId && questionnaireIds.has(catalogQId)) return true;
    const qKeys = collectRfiTemplateKeys(q);
    if (q.hash_id && templateHashIds.has(String(q.hash_id))) return true;
    if (qKeys.some((k) => templateHashIds.has(k))) return true;
    if (q.services && templateNames.has(q.services)) return true;
    if (q.name && templateNames.has(q.name)) return true;
    return false;
  };

  return questionnaires.filter(matchesUserRfis).map(mapToOption);
}

/**
 * Adds dropdown options for templates that appear on RFI rows but are missing from the catalog response.
 */
export function mergeTemplateOptionsWithRfiFallback(matchedOptions, rfiItems) {
  const safe = matchedOptions ?? [];
  const covered = new Set();
  for (const o of safe) {
    for (const k of collectRfiTemplateKeys(o)) {
      covered.add(k);
    }
  }
  const extras = [];
  const seenFilterValues = new Set();
  for (const row of rfiItems ?? []) {
    const label = rfiTemplateLabel(row);
    if (!label) continue;
    const keys = collectRfiTemplateKeys(row);
    const filterValue = rfiTemplateFilterValue(row);
    if (filterValue == null || filterValue === "") continue;
    const fv = String(filterValue);
    if (seenFilterValues.has(fv)) continue;
    const alreadyCovered = keys.some((k) => covered.has(k));
    if (alreadyCovered) continue;
    seenFilterValues.add(fv);
    keys.forEach((k) => covered.add(k));
    extras.push({
      ...row,
      id: filterValue,
      hash_id: filterValue,
      name: label,
      label,
    });
  }
  return [...safe, ...extras];
}

/**
 * While all-pages RFIs are loading, show full template list mapped to options; then narrow.
 */
export function buildTemplateFilterOptions(
  questionnaires,
  rfiItems,
  isAllRfisFetched,
  mapToOption
) {
  let options;
  if (!isAllRfisFetched) {
    options = (questionnaires || []).map(mapToOption);
  } else {
    const matched = filterQuestionnairesByRfiRows(questionnaires, rfiItems, mapToOption);
    options = matched.length > 0 ? matched : [];
  }
  const matched = filterQuestionnairesByRfiRows(questionnaires, rfiItems, mapToOption);
  const withFallback = mergeTemplateOptionsWithRfiFallback(matched, rfiItems);
  const labeled = applyRfiTemplateLabelsToOptions(withFallback, rfiItems);
  options = labeled.length > 0 ? labeled : options;
  // Deduplicate by label/name and sort alphabetically
  const seen = new Set();
  return options
    .filter((opt) => {
      const key = opt.label || opt.name;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => (a.label || a.name || "").localeCompare(b.label || b.name || ""));
}

/** Maps questionnaire/template API rows to filter dropdown options (My RFIs, Provider RFI Requests). */
export function mapQuestionnaireTemplateToFilterOption(item) {
  return {
    ...item,
    id: item.id,
    hash_id: item.hash_id,
    name: item.name || item.services,
    label: item.name || item.services,
  };
}
